import { useMemo } from 'react';

import { appLinks } from '@/configs/footerAppLinks';
import {
  desktopNavItems,
  mobileNavItems,
  unAuthenticatedNavItems,
} from '@/configs/nav/nav-items';
import { useExternalCurrencyStore, useSettingsStore } from '@/store';
import { filterFooterAppLinks } from '../utils/filterFooterAppLinks';
import { filterNavItems } from '../utils/filterNavItems';
import { useAuth } from './useAuth';
import { usePermission } from './useRoutePermission';
import useTwinDetails from './useTwinDetails';

export default function useMenuItems() {
  const { settings } = useSettingsStore();
  const { isAuthenticated } = useAuth();
  const allowedPages = settings?.ui?.pages_sections?.pages;
  const userPays = settings?.suVariables?.userPays;

  const defaultHomePage = settings?.ui?.pages_sections?.defaultHomePage;

  const checkPermission = usePermission();

  const { selfTwinData, selfTwinDataLoading: isLoading } = useTwinDetails();

  const myTwinDetails = selfTwinData?.data?.data ?? null;

  const isUserHasTwin = myTwinDetails && Object.keys(myTwinDetails).length > 0;

  const isTwinLandingVisible = checkPermission('get_twin') && !isUserHasTwin;

  const { externalCurrencyEnabled } = useExternalCurrencyStore();

  const isMyTwinVisible = isUserHasTwin;

  const desktopMenuItems = useMemo(() => {
    const desktopItems = desktopNavItems.filter((menu) => {
      if (defaultHomePage === 'chat' && menu.key === 'chat') {
        return false;
      }
      if (menu.key === 'my_twin') {
        return isMyTwinVisible;
      }
      if (menu.key === 'get_twin') {
        return isTwinLandingVisible;
      }
      return true;
    });
    return filterNavItems(
      isAuthenticated ? desktopItems : unAuthenticatedNavItems,
      allowedPages,
      externalCurrencyEnabled
    );
  }, [allowedPages, defaultHomePage, externalCurrencyEnabled, isAuthenticated, isMyTwinVisible, isTwinLandingVisible]);

  const filteredMobileNavItems = useMemo(() => {
    return mobileNavItems.filter((menu) => {
       if (menu.key === 'buy_points') {
      return userPays && !externalCurrencyEnabled;
    }
      if (defaultHomePage === 'chat' && menu.key === 'chat') {
        return false;
      }
      if (menu.key === 'my_twin') {
        return isMyTwinVisible;
      }
      if (menu.key === 'get_twin') {
        return isTwinLandingVisible;
      }
      return true;
    });
  }, [defaultHomePage, userPays, externalCurrencyEnabled, isMyTwinVisible, isTwinLandingVisible]);

  const mobileMenuItems = useMemo(() => {
    return filterNavItems(
      isAuthenticated ? filteredMobileNavItems : unAuthenticatedNavItems,
      allowedPages,
      externalCurrencyEnabled
    );
  }, [isAuthenticated, filteredMobileNavItems, allowedPages, externalCurrencyEnabled]);

  const updatedAppLinks = useMemo(() => {
    return {
      sections: appLinks.sections.map((section) => ({
        ...section,
        links: section.links.filter((link) => {
          if (link.key === 'buy_points') {
          return userPays && !externalCurrencyEnabled;
        }
          if (defaultHomePage === 'chat' && link.key === 'chat') {
            return false;
          }
          if (link.key === 'my_twin') {
            return isMyTwinVisible;
          }
          if (link.key === 'get_twin') {
            return isTwinLandingVisible;
          }
          return true;
        }),
      })),
    };
  }, [defaultHomePage, userPays, externalCurrencyEnabled, isMyTwinVisible, isTwinLandingVisible]);

  const footerMenuLinks = filterFooterAppLinks(updatedAppLinks, allowedPages, externalCurrencyEnabled);

  return { desktopMenuItems, mobileMenuItems, footerMenuLinks, isLoading };
}
